// src/features/dashboard/components/ManagerDashboard/IdleTimeReport/IdleKpiCards.tsx

import {
  Clock3,
  TimerReset,
  Users,
  Trophy,
} from 'lucide-react';

import {
  IdleTimeKpiResponse,
} from '../../../types/Manager/idleTime.types';

type Props = {
  data: IdleTimeKpiResponse | null;
};

const getTrendColor = (
  trend?: string,
) => {

  switch (
    trend?.toLowerCase()
  ) {

    case 'increase':

      return 'text-red-500';

    case 'decrease':

      return 'text-green-600';

    default:

      return 'text-gray-500';
  }
};

const IdleKpiCards = ({
  data,
}: Props) => {

  const kpi: IdleTimeKpiResponse =
    data ?? {

      spanIdlePercentage: 0,

      previousSpanIdlePercentage: 0,

      spanIdleTrend: 0,

      averageIdleTime:
        '00:00:00',

      withinTarget: 0,

      exceedingTarget: 0,

      teamLeadCode: '',

      teamLeadName: '-',

      associates: 0,

      bestTeamIdlePercentage: 0,

      targetPercentage: 0,

      totalAssociates: 0,

      trendDirection: 'Same',

      bestTeamStatus: '',
    };

  const formatHoursMinutes = (
    time: string,
  ): string => {

    if (!time) {
      return '0m';
    }

    const [
      hours = '0',
      minutes = '0',
    ] = time.split(':');

    const h =
      Number(hours);

    const m =
      Number(minutes);

    if (
      h > 0 &&
      m > 0
    ) {
      return `${h}h ${m}m`;
    }

    if (h > 0) {
      return `${h}h`;
    }

    return `${m}m`;
  };

  const cards = [

    {
      title:
        'Span Idle %',

      value:
        `${kpi.spanIdlePercentage.toFixed(2)}%`,

      subtitle:
        `${kpi.trendDirection === 'Increase'
          ? '▲'
          : kpi.trendDirection === 'Decrease'
            ? '▼'
            : ''
        } ${kpi.spanIdleTrend.toFixed(1)} pts vs last wk · Target ≤${kpi.targetPercentage}%`,

      color:
        'bg-orange-100 text-orange-600',

      icon:
        <Clock3 size={18} />,

      trend:
        kpi.trendDirection,
    },

    {
      title:
        'Avg Idle Time / Day',

      value:
        formatHoursMinutes(
          kpi.averageIdleTime,
        ),

      subtitle:
        'per associate',

      color:
        'bg-red-100 text-red-600',

      icon:
        <TimerReset size={18} />,

      trend:
        '',
    },

    {
      title:
        'Associates Within Target',

      value:
        kpi.withinTarget.toString(),

      subtitle:
        `idle < ${kpi.targetPercentage}% this week`,

      color:
        'bg-blue-100 text-blue-600',

      icon:
        <Users size={18} />,

      trend:
        '',
    },

    {
      title:
        'Exceeding Target',

      value:
        kpi.exceedingTarget.toString(),

      subtitle:
        `idle > ${kpi.targetPercentage}%`,

      color:
        'bg-purple-100 text-purple-600',

      icon:
        <Users size={18} />,

      trend:
        '',
    },

    {
      title:
        'Best Team',

      value:
        `${kpi.bestTeamIdlePercentage.toFixed(0)} %`,

      subtitle:
        kpi.teamLeadName,

      color:
        'bg-green-100 text-green-600',

      icon:
        <Trophy size={18} />,

      trend:
        '',
    },
  ];

  return (

    <div
      className="
        grid
        w-full

        grid-cols-1

        gap-4

        sm:grid-cols-2

        md:grid-cols-3

        lg:grid-cols-5

        /* ==========================================================
           110% ZOOM
        =========================================================== */

        [@media(max-width:1100px)]:gap-2.5

        /* ==========================================================
           125% - 150% ZOOM
        =========================================================== */

        [@media(max-width:1000px)]:gap-2

        /* Keep all 5 cards in one row on compact desktop */
        [@media(min-width:900px)]:grid-cols-5
      "
    >

      {cards.map(
        (card) => (

          <div
            key={card.title}
            className="
              min-w-0
              w-full
              overflow-hidden

              border
              border-[#d9dde3]

              bg-white

              rounded-[18px]

              px-4
              py-4

              h-[110px]

              flex
              items-start
              gap-4

              transition-all

              hover:shadow-sm

              /* ==================================================
                 110% ZOOM
              =================================================== */

              [@media(max-width:1100px)]:h-[96px]
              [@media(max-width:1100px)]:rounded-[14px]
              [@media(max-width:1100px)]:px-3
              [@media(max-width:1100px)]:py-3
              [@media(max-width:1100px)]:gap-2.5

              /* ==================================================
                 125% - 150% ZOOM
              =================================================== */

              [@media(max-width:1000px)]:h-[88px]
              [@media(max-width:1000px)]:rounded-[12px]
              [@media(max-width:1000px)]:px-2.5
              [@media(max-width:1000px)]:py-2.5
              [@media(max-width:1000px)]:gap-2
            "
          >

            {/* ====================================================
                ICON
            ===================================================== */}

            <div
              className={`
                flex
                h-9
                w-9
                shrink-0
                items-center
                justify-center
                rounded-full

                ${card.color}

                /* 110% */

                [@media(max-width:1100px)]:h-8
                [@media(max-width:1100px)]:w-8

                /* 125% - 150% */

                [@media(max-width:1000px)]:h-7
                [@media(max-width:1000px)]:w-7
              `}
            >

              <span
                className="
                  flex
                  items-center
                  justify-center

                  [@media(max-width:1100px)]:scale-[0.9]

                  [@media(max-width:1000px)]:scale-[0.8]
                "
              >
                {card.icon}
              </span>

            </div>


            {/* ====================================================
                CONTENT
            ===================================================== */}

            <div
              className="
                flex
                min-w-0
                flex-1
                flex-col
                justify-between
                overflow-hidden
              "
            >

              {/* ==================================================
                  TITLE
              =================================================== */}

              <div
                className="
                  min-w-0
                  overflow-hidden

                  text-[11px]
                  font-semibold
                  leading-4

                  text-[#111827]

                  /* Allow complete title */
                  whitespace-normal

                  /* Maximum 2 lines */
                  line-clamp-2

                  [@media(max-width:1100px)]:text-[10px]
                  [@media(max-width:1100px)]:leading-3.5

                  [@media(max-width:1000px)]:text-[9px]
                  [@media(max-width:1000px)]:leading-3
                "
                title={card.title}
              >
                {card.title}
              </div>


              {/* ==================================================
                  VALUE
              =================================================== */}

              <div
                className="
                  mt-1
                  min-w-0

                  [@media(max-width:1000px)]:mt-0
                "
              >

                <div
                  className="
                    min-w-0
                    overflow-hidden
                    text-ellipsis
                    whitespace-nowrap

                    text-[16px]
                    font-bold
                    leading-5

                    text-[#111827]

                    [@media(max-width:1100px)]:text-[14px]
                    [@media(max-width:1100px)]:leading-4

                    [@media(max-width:1000px)]:text-[13px]
                    [@media(max-width:1000px)]:leading-4
                  "
                >
                  {card.value}
                </div>

              </div>


              {/* ==================================================
                  SUBTITLE
              =================================================== */}

              <div
                className={`
                  min-w-0
                  overflow-hidden

                  text-[11px]
                  leading-4

                  ${getTrendColor(
                    card.trend,
                  )}

                  /* Allow subtitle to wrap */
                  whitespace-normal

                  /* Maximum 2 lines */
                  line-clamp-2

                  [@media(max-width:1100px)]:text-[9px]
                  [@media(max-width:1100px)]:leading-3

                  [@media(max-width:1000px)]:text-[8px]
                  [@media(max-width:1000px)]:leading-3
                `}
                title={card.subtitle}
              >
                {card.subtitle}
              </div>

            </div>

          </div>

        ),
      )}

    </div>
  );
};

export default IdleKpiCards;